import { useUserStore } from '@/stores/userStore';
import i18n from '@/i18n';

const t = (key, params) => i18n.global.t(key, params);
import { defineStore } from 'pinia';
import axios from '@/services/axios';
import ApiClient from '@/services/apiClient';
import EventType from '@/models/eventType';
import AccessGroup from '@/models/accessGroup';
import Door from '@/models/door';
import RemoteOutput from '@/models/remoteOutput';

export const useClientStore = defineStore('client', {
    state: () => ({
        event_types: [],
        access_groups: [],
        doors: [],
        readers: [],
        extensions: [],
        outputs: [],
        remote_outputs: [],
        inputs: [],
        external_alarms: [],
    }),

    actions: {
        async loadClientData(facilityId, hubId) {
            await this.loadEventTypes(facilityId);
            await this.loadAccessGroups(facilityId);
            await this.loadDoors(facilityId, hubId);
            this.loadReaders(facilityId, hubId);
            this.loadExtensions(facilityId, hubId);
            this.loadOutputs(facilityId, hubId);
            this.loadRemoteOutputs(facilityId, hubId);
            this.loadInputs(facilityId, hubId);
            this.loadExternalAlarms(facilityId);
        },

        loadExternalAlarms(facilityId) {
            const userStore = useUserStore();
            this.external_alarms = userStore.getExternalAlarms(facilityId) || [];
        },

        getExternalAlarms(id) {
            if(id != null && id !== "") {
                return this.external_alarms.filter(a => a.id === id);
            } else {
                return this.external_alarms;
            }
        },

        async loadEventTypes(facilityId) {
            try {
                const response = await axios.get(`/conf/facilities/${facilityId}/event_types`);
                this.event_types = ApiClient.convertToType(response.data, [EventType]);
            } catch (error) {
                console.error("loadEventTypes failed", error);
                let message = t('api.loadEventTypesFailed');
                if (error.response) message += ` (${error.response.status})`
                throw message;
            }
        },

        getEventTypes(ids) {
            return this.event_types.filter(e => ids.includes(e.id));
        },

        getEventTypeOptions(filteredTypes = []) {
            return this.event_types.filter(e => !filteredTypes.includes(e.id));
        },

        async loadAccessGroups(facilityId) {
            try {
                const response = await axios.get(`/conf/facilities/${facilityId}/access_groups`);
                this.access_groups = ApiClient.convertToType(response.data, [AccessGroup]);
            } catch (error) {
                console.error("loadAccessGroups failed", error);
                let message = t('api.loadGroupsFailed');
                if (error.response) message += ` (${error.response.status})`
                throw message;
            }
        },

        getAccessGroups(ids) {
            if(ids) {
                return this.access_groups.filter(e => ids.includes(e.id));
            } else {
                return this.access_groups;
            }
        },

        async loadDoors(facilityId, hubId) {
            try {
                const response = await axios.get(`/conf/facilities/${facilityId}/components/${hubId}/doors`);
                this.doors = ApiClient.convertToType(response.data, [Door]);
            } catch (error) {
                console.error("loadDoors failed", error);
                let message = t('api.loadDoorsFailed');
                if (error.response) message += ` (${error.response.status})`
                throw message;
            }
        },

        getDoors(ids) {
            if(ids) {
                return this.doors.filter(e => ids.includes(e.id));
            } else {
                return this.doors;
            }
        },

        loadReaders(facilityId, hubId) {
            const userStore = useUserStore();            
            const hub = userStore.getHub(facilityId, hubId);
            this.readers = hub? hub.readers : [];
        },

        getReaders(positions) {
            if(positions) {
                return this.readers.filter(e => positions.includes(e.position));
            } else {
                return this.readers;
            }
        },

        loadExtensions(facilityId, hubId) {
            const userStore = useUserStore();            
            const hub = userStore.getHub(facilityId, hubId);
            this.extensions = hub? hub.extensions : [];
        },

        getExtensions(serial_number) {
            if(serial_number) {
                return this.extensions.filter(e => e.serial_number == serial_number);
            } else {
                return this.extensions;
            }
        },

        loadOutputs(facilityId, hubId) {
            const userStore = useUserStore();            
            const hub = userStore.getHub(facilityId, hubId);
            this.outputs = [];
            if(hub) {
                this.outputs = hub.getAvailableOutputs();
            }
        },

        getOutputs(outputs) {
            if(outputs) {
                return this.outputs.filter(o => outputs.includes(o.output_number));
            } else {
                return this.outputs;
            }
        },

        loadRemoteOutputs(facilityId, hubId) {
            const userStore = useUserStore();
            const hubs = userStore.getHubs(facilityId);
            this.remote_outputs = [];
            hubs.forEach(hub => {
                if(hub.id !== hubId) {
                    const outputs = hub.getAvailableOutputs();
                    outputs.forEach(output => {
                        this.remote_outputs.push(new RemoteOutput(hub.serial_number, output.output_number, `${output.label} (${hub.label})`));
                    });
                }
            });
        },

        getRemoteOutputs(remote_outputs) {
            if(remote_outputs) {
                return this.remote_outputs.filter(remote => remote_outputs.some(output => output.output_id === remote.output_id));
            } else {
                return this.remote_outputs;
            }
        },

        loadInputs(facilityId, hubId) {
            const userStore = useUserStore();            
            const hub = userStore.getHub(facilityId, hubId);
            this.inputs = [];
            if(hub) {
                this.inputs = hub.getAvailableInputs();
            }
        },

        getInputs(input) {
            if(input) {
                return this.inputs.filter(i => i.input_number === input);
            } else {
                return this.inputs;
            }
        },
    },
});

